import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { RootState } from '..';
import {
  addTutorAchievementAsAdmin,
  addTutorExperienceAsAdmin,
  addTutorLanguageAsAdmin,
  addTutorQualificationAsAdmin,
  addUserSubjectAsAdmin,
  adminCreateUserAdminNote,
  adminDeleteUserAdminNote,
  createUserAsAdmin,
  deleteTutorAchievementAsAdmin,
  deleteTutorExperienceAsAdmin,
  deleteTutorLanguageAsAdmin,
  deleteTutorQualificationAsAdmin,
  deleteUserSubjectAsAdmin,
  getUserByIdAsAdmin,
  getUserListAsAdmin,
  getUserListForDropDownAsAdmin,
  updateTutorAchievementAsAdmin,
  updateTutorExperienceAsAdmin,
  updateTutorLanguageAsAdmin,
  updateTutorQualificationAsAdmin,
  updateUserAsAdmin,
  updateUserPlanDataAsAdmin,
  updateUserOneToOneTutorRatesAsAdmin,
  updateUserGroupTutorRatesAsAdmin,
  adminResendUserTempPassword,
} from './AdminUsersApi';
import {
  deleteUser,
  getEnrolmentCourseRelatedStudents,
  getEnrolmentCourseRelatedTutors,
  getUser,
  updateUserPageGuides,
} from './UserApi';
import {
  removeAdminPortalEditModeValueFromLocalStorage,
  removeAuthTokenToLocalStorage,
  removeAuthUserToLocalStorage,
} from '../../helper';
import { IGroup } from '../V2/GroupsSlice';
import { IOneToOneTutorRateStudentType } from '../V2/OneToOneTutorRateStudentTypesSlice';
import { IGroupTutorRateCourseType } from '../V2/GroupTutorRateCourseTypesSlice';
import { IFlexiblePlan } from '../V2/FlexiblePlansSlice';
import { ISubscriptionPlan } from '../V2/SubscriptionPlansSlice';
import { IRole } from '../V2/RolesSlice';

export type TIdentifiedUserRole = 'STUDENT' | 'TUTOR';

export const IDENTIFIED_USER_ROLES = {
  STUDENT: 'STUDENT' as TIdentifiedUserRole,
  TUTOR: 'TUTOR' as TIdentifiedUserRole,
};

export interface IBaseUser {
  _id: string;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  avatar?: string;
  createdAt: string;
  updatedAt?: string;
  role: IRole;
  lastLogin?: string;
  country?: string;
}

export interface IUserCurriculum {
  curriculum: string;
  grade: string;
  subject: string;
  subjectCode: string;
}

export interface IUserExperience {
  _id?: string;
  jobTitle: string;
  workPlace: string;
  startDate: string;
  endDate: string;
}

export interface IUserQualification {
  _id?: string;
  qualification: string;
  institute: string;
  startDate: string;
  endDate: string;
}

export interface IUserAchievement {
  _id?: string;
  awardTitle: string;
  awardBody: string;
  awardDate: string;
}

export interface IUserLanguage {
  _id?: string;
  language: string;
  proficiency: string;
}

interface IUserProfile {
  experiences?: IUserExperience[];
  qualifications?: IUserQualification[];
  achievements?: IUserAchievement[];
  languages?: IUserLanguage[];
  feedbackPreferences?: {
    date: string;
    options: string[];
    other?: string;
  };
}

export interface IUserSubject {
  _id?: string;
  subject: string;
  curriculum: string;
  grade: string;
}

export interface IUserTutorDetails {
  teachingStatus?: string;
  availability?: string;
  isCameraOn?: boolean;
  trialNoticePeriod?: number;
  classNoticePeriod?: number;
  strengths?: string[];
  bio?: string;
  videoLink?: string;
  adminRating?: string;
  lessonDelivered?: number;
  noOfReviews?: number;
  rating?: number;
  noOfHours?: number;
  tutorIntroduction?: string;
  taughtStudentsCount?: number;
  trialLessonsCount?: number;
  teachingStrengths?: string[];
  perfectFor?: string[];
}
export interface IUserBankDetails {
  bankCountry?: string;
  bankName?: string;
  bankCode?: string;
  branchCode?: string;
  bankBranch?: string;
  accountNo?: string;
  accountHolderName?: string;
}
export interface IUserFlexibleRate {
  amount: number;
  currency: string;
  planId: string | IFlexiblePlan;
  country?: string;
  curriculum?: string;
  grade?: string;
}

export interface IUserSubscriptionRate {
  amount: number;
  currency: string;
  planId: string | ISubscriptionPlan;
  country?: string;
  curriculum?: string;
  grade?: string;
}

export interface IUserAdminNote {
  _id: string;
  description: string;
  createdBy: string | IUser;
  createdAt: string;
}

export interface IUserOneToOneTutorRateAmount {
  rateVersion: string;
  amount?: number;
  currency?: string;
  hideToUser: boolean;
}

export interface IUserOneToOneTutorRate {
  tutorCountry: string;
  studentType: string | IOneToOneTutorRateStudentType;
  amounts: IUserOneToOneTutorRateAmount[];
}

export interface IUserGroupTutorRateAmount {
  rateVersion: string;
  amount?: number;
  currency?: string;
  hideToUser: boolean;
}

export interface IUserGroupTutorRate {
  tutorCountry: string;
  courseType: string | IGroupTutorRateCourseType;
  amounts: IUserGroupTutorRateAmount[];
}

export interface IUserNotifications {
  email: {
    classReminders: {
      [key: string]: boolean;
    };
    homeworks: {
      [key: string]: boolean;
    };
    payments: {
      [key: string]: boolean;
    };
  };
  whatsapp: {
    classReminders: {
      [key: string]: boolean;
    };
    homeworks: {
      [key: string]: boolean;
    };
    payments: {
      [key: string]: boolean;
    };
  };
}

export interface IUserPremiumAccess {
  startAt: string;
  expiresAt: string;
  renewAt: string;
}

export interface IUserPageGuides {
  dashboardCompleted?: boolean;
  navigationCompleted?: boolean;
  coursesCompleted?: boolean;
  viewCourseCompleted?: boolean;
}

export interface IUser extends IBaseUser {
  billingEmail?: string;
  isQuiztopiyaPendingTrial?: boolean;
  assessmentEngineDate?: string;
  quiztopiyaTrialExpiration?: string;
  curriculums?: Array<IUserCurriculum>;
  isContinueSubmissionWithTimerEnabled?: boolean;
  isLeadGenSubmitted?: boolean;
  students?: Array<{
    username: string;
  }>;
  gender?: string;
  dateOfBirth?: string;
  tutopiyaUserId?: string;
  phoneNumber?: string;
  country?: string;
  curriculum?: string;
  grade?: string;
  examDate?: string;
  schoolName?: string;
  parentName?: string;
  parentEmail?: string;
  parentPhoneNumber?: string;
  nationality?: string;
  emergencyNumber?: string;
  notifications?: IUserNotifications;
  profile?: IUserProfile;
  oneToOneStudentType?: string | IOneToOneTutorRateStudentType;
  subjects?: IUserSubject[];
  tutorDetails?: IUserTutorDetails;
  bankDetails?: IUserBankDetails;
  remainingTime?: number;
  groups?: string[] | IGroup[];
  flexibleRates?: IUserFlexibleRate[];
  subscriptionRates?: IUserSubscriptionRate[];
  adminNotes?: IUserAdminNote[];
  oneToOneTutorRates?: IUserOneToOneTutorRate[];
  groupTutorRates?: IUserGroupTutorRate[];
  isTrialApplied?: boolean;
  adminTag?: string | IUser;
  premiumAccess?: IUserPremiumAccess;
  pageGuides?: IUserPageGuides;
  msTeamsEmail?: string;
  referenceId: string;
}

export interface IUsersState {
  status: 'idle' | 'loading' | 'rejected';
  count: number;
  user: IUser | undefined;
  enrolmentCourseRelatedStudentList: IUser[];
  enrolmentCourseRelatedTutorList: IUser[];
  // Admin
  adminUserList?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IUser>;
  };
  userListDataForAdminForDropDown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IBaseUser>;
  };
}

const initialState: IUsersState = {
  status: 'idle',
  count: 0,
  user: undefined,
  enrolmentCourseRelatedStudentList: [],
  enrolmentCourseRelatedTutorList: [],
  userListDataForAdminForDropDown: undefined,
  adminUserList: undefined,
};

export const UsersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    resetEnrolmentCourseRelatedStudentList: (state) => {
      state.enrolmentCourseRelatedStudentList = [];
    },
    resetEnrolmentCourseRelatedTutorList: (state) => {
      state.enrolmentCourseRelatedTutorList = [];
    },
    resetUserListDataForAdmin: (state) => {
      state.userListDataForAdminForDropDown = undefined;
    },
    resetUser: (state) => {
      state.user = undefined;
    },
  },
  extraReducers: (builder) => {
    builder

      // GET ENROLMENT COURSE RELATED STUDENTS
      .addCase(getEnrolmentCourseRelatedStudents.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getEnrolmentCourseRelatedStudents.rejected, (state) => {
        state.status = 'rejected';
      })
      .addCase(getEnrolmentCourseRelatedStudents.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        state.enrolmentCourseRelatedStudentList = result;
      })

      // GET ENROLMENT COURSE RELATED TUTORS
      .addCase(getEnrolmentCourseRelatedTutors.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getEnrolmentCourseRelatedTutors.rejected, (state) => {
        state.status = 'rejected';
      })
      .addCase(getEnrolmentCourseRelatedTutors.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        state.enrolmentCourseRelatedTutorList = result;
      })

      // V2
      // Admin
      // Get user list For DropDown
      .addCase(getUserListForDropDownAsAdmin.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getUserListForDropDownAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;
        const newUserListDataForAdminForDropDown = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.userListDataForAdminForDropDown = newUserListDataForAdminForDropDown;
      })
      .addCase(getUserListForDropDownAsAdmin.rejected, (state) => {
        state.status = 'rejected';
      })

      // Get admin user list
      .addCase(getUserListAsAdmin.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getUserListAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        const result = action.payload;
        const newAdminUserList = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };
        state.adminUserList = newAdminUserList;
      })
      .addCase(getUserListAsAdmin.rejected, (state) => {
        state.status = 'rejected';
      })

      // Admin user create
      .addCase(createUserAsAdmin.fulfilled, (state, action) => {
        const result = action.payload;

        if (result && state.adminUserList) {
          const newAdminUserList = {
            ...state.adminUserList,
          };

          newAdminUserList.list.unshift(result);

          if (newAdminUserList.totalDocs) {
            newAdminUserList.totalDocs += 1;
          } else {
            newAdminUserList.totalDocs = 1;
          }

          state.adminUserList = newAdminUserList;
        }
      })

      // DELETE USER
      .addCase(deleteUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.status = 'idle';
        removeAuthTokenToLocalStorage();
        removeAuthUserToLocalStorage();
        removeAdminPortalEditModeValueFromLocalStorage();
        window.location.href = `${process.env.PUBLIC_URL}/`;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.status = 'rejected';
      })

      // Resend usertemp password
      .addCase(adminResendUserTempPassword.pending, (state, action) => {
        state.status = 'loading';
      })
      .addCase(adminResendUserTempPassword.fulfilled, (state, action) => {
        state.status = 'idle';
      })
      .addCase(adminResendUserTempPassword.rejected, (state, action) => {
        state.status = 'rejected';
      });

    const userActions = [
      getUser,
      getUserByIdAsAdmin,
      updateUserAsAdmin,
      addTutorExperienceAsAdmin,
      updateTutorExperienceAsAdmin,
      deleteTutorExperienceAsAdmin,
      addTutorQualificationAsAdmin,
      updateTutorQualificationAsAdmin,
      deleteTutorQualificationAsAdmin,
      addTutorAchievementAsAdmin,
      updateTutorAchievementAsAdmin,
      deleteTutorAchievementAsAdmin,
      addTutorLanguageAsAdmin,
      updateTutorLanguageAsAdmin,
      deleteTutorLanguageAsAdmin,
      addUserSubjectAsAdmin,
      deleteUserSubjectAsAdmin,
      updateUserPlanDataAsAdmin,
      adminCreateUserAdminNote,
      adminDeleteUserAdminNote,
      updateUserOneToOneTutorRatesAsAdmin,
      updateUserGroupTutorRatesAsAdmin,
      updateUserPageGuides,
    ] as const;

    builder
      // MATCHER
      .addMatcher(isFulfilled(...userActions), (state, action) => {
        state.status = 'idle';
        state.user = action.payload;
      })
      .addMatcher(isPending(...userActions, createUserAsAdmin), (state) => {
        state.status = 'loading';
      })
      .addMatcher(isRejected(...userActions, createUserAsAdmin), (state) => {
        state.status = 'rejected';
      });
  },
});

export const getUsersState = (state: RootState): RootState['users'] =>
  state.users;

export const {
  resetUserListDataForAdmin,
  resetEnrolmentCourseRelatedStudentList,
  resetEnrolmentCourseRelatedTutorList,
  resetUser,
} = UsersSlice.actions;

export * from './AdminUsersApi';
export default UsersSlice.reducer;
